import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCrown, 
  faCalendarAlt, 
  faCreditCard, 
  faExternalLinkAlt,
  faInfoCircle,
  faGlobe,
  faKey
} from '@fortawesome/free-solid-svg-icons';
import { faApple, faGoogle } from '@fortawesome/free-brands-svg-icons';
import moment from 'moment';

/**
 * PremiumDetails Component
 * 
 * Displays comprehensive subscription information for premium users including:
 * - Platform details (Web/Apple/Android/License)
 * - Subscription status and expiry information
 * - Billing type (recurring/one-time)
 * - Management options with platform-specific instructions
 * - Additional details for license codes (period, assigned email, etc.)
 * 
 * @param {Object} subscription - Subscription data object
 * @param {Object} theme - Theme object for styling
 * @param {Function} onManageSubscription - Callback for web subscription management
 * @param {boolean} loading - Loading state for manage button
 */
const PremiumDetails = ({ 
  subscription, 
  theme, 
  onManageSubscription, 
  loading = false 
}) => {
  const [showInstructions, setShowInstructions] = useState(false);

  if (!subscription || !subscription.subscription_expiration) return null;

  const {
    paymentMode,
    paymentType,
    plan,
    subscription_expiration,
    period,
    licenseCode,
    assigned_email
  } = subscription;

  const isRecurring = paymentType === 'recurring';
  const expiryDate = moment(parseInt(subscription_expiration));
  const isExpired = expiryDate.isBefore(moment());
  const daysRemaining = Math.ceil(expiryDate.diff(moment(), 'days', true));
  const isExpiringSoon = daysRemaining <= 7 && daysRemaining > 0;
  
  // Handle invalid dates
  if (!expiryDate.isValid()) {
    return null;
  }

  const getPlatformInfo = () => {
    switch (paymentMode?.toLowerCase()) {
      case 'app_store':
        return {
          name: 'Apple App Store',
          icon: faApple,
          color: '#000000',
          instructions: [
            'Open Settings on your iPhone/iPad',
            'Tap your Apple ID at the top',
            'Tap "Subscriptions"',
            'Find this app and tap "Manage"',
            'You can cancel or change your subscription here'
          ]
        };
      case 'play_store':
        return {
          name: 'Google Play Store',
          icon: faGoogle,
          color: '#4285F4',
          instructions: [
            'Open Google Play Store',
            'Tap your profile icon',
            'Tap "Payments & subscriptions"',
            'Tap "Subscriptions"',
            'Find this app and tap "Manage"',
            'You can cancel or change your subscription here'
          ]
        };
      case 'stripe':
        return {
          name: 'Web Subscription',
          icon: faGlobe,
          color: '#6772E5',
          instructions: []
        };
      case 'bulk_purchase':
        return {
          name: 'License Code',
          icon: faKey,
          color: '#FF6B35',
          instructions: []
        };
      default:
        return {
          name: 'Unknown Platform',
          icon: faInfoCircle,
          color: '#6B7280',
          instructions: []
        };
    }
  };

  const platformInfo = getPlatformInfo();

  const styles = {
    container: {
      background: theme?.background?.orange,
      borderRadius: '1.25rem',
      padding: '2rem',
      color: theme?.text?.primary,
      position: 'relative',
      overflow: 'hidden',
      minHeight: '400px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    backgroundPattern: {
      position: 'absolute',
      top: 0,
      right: 0,
      width: '100px',
      height: '100px',
      opacity: 0.1,
      backgroundImage: `
        radial-gradient(circle at 50% 50%, rgba(255,255,255,0.3) 0%, transparent 70%)
      `,
    },
    content: {
      position: 'relative',
      zIndex: 1,
    },
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      marginBottom: '1rem',
    },
    crownIcon: {
      color: theme?.primary?.orange?.[500],
      fontSize: '1.5rem',
      marginTop: '0.25rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700',
      margin: 0,
      lineHeight: 1.2,
    },
    planBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '0.25rem 0.75rem',
      borderRadius: '0.5rem',
      fontSize: '0.75rem',
      fontWeight: '500',
      background: theme?.primary?.orange?.[100] || '#FEF3C7',
      color: theme?.primary?.orange?.[700] || '#D97706',
      marginLeft: 'auto'
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      flex: 1,
      justifyContent: 'space-between'
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.75rem 0',
      borderBottom: `1px solid ${theme?.primary?.orange?.[200]}40`
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.875rem',
      fontWeight: '500',
      color: theme?.text?.primary || '#111827',
      opacity: 0.9
    },
    value: {
      fontSize: '0.875rem',
      fontWeight: '600',
      color: theme?.text?.primary || '#111827',
      textAlign: 'right'
    },
    platformInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    platformIcon: {
      fontSize: '1rem'
    },
    manageButton: {
      width: '100%',
      maxWidth: '300px',
      padding: '0.875rem',
      borderRadius: '0.5rem',
      fontWeight: '600',
      fontSize: '1rem',
      cursor: loading ? 'not-allowed' : 'pointer',
      transition: 'all 0.3s ease',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      background: `linear-gradient(135deg, ${theme?.primary?.orange?.[300]}, ${theme?.primary?.orange?.[400]})`,
      color: theme?.neutral?.black || '#000',
      opacity: loading ? 0.6 : 1,
      animation: 'pulse 2s infinite',
    },
    buttonIcon: {
      fontSize: '0.75rem',
    },
    instructionsButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      borderRadius: '0.5rem',
      fontSize: '0.875rem',
      fontWeight: '500',
      background: 'transparent',
      color: theme?.primary?.blue?.[600] || '#2563EB',
      border: `1px solid ${theme?.primary?.blue?.[300] || '#93C5FD'}`,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textDecoration: 'none'
    },
    instructionsModal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    instructionsContent: {
      background: theme?.background?.card || '#ffffff',
      borderRadius: '1rem',
      padding: '2rem',
      maxWidth: '500px',
      width: '90%',
      maxHeight: '80vh',
      overflow: 'auto',
      boxShadow: theme?.shadows?.xl || '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
    },
    instructionsHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      marginBottom: '1.5rem',
      paddingBottom: '1rem',
      borderBottom: `1px solid ${theme?.border?.light || '#e5e7eb'}`
    },
    instructionsList: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    instructionItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.75rem',
      padding: '0.75rem 0',
      borderBottom: `1px solid ${theme?.border?.light || '#e5e7eb'}`
    },
    instructionNumber: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '1.5rem',
      height: '1.5rem',
      borderRadius: '50%',
      background: theme?.primary?.blue?.[500] || '#3B82F6',
      color: 'white',
      fontSize: '0.75rem',
      fontWeight: '600',
      flexShrink: 0
    },
    instructionText: {
      fontSize: '0.875rem',
      color: theme?.text?.primary || '#111827',
      lineHeight: '1.5',
      margin: 0
    },
    closeButton: {
      position: 'absolute',
      top: '1rem',
      right: '1rem',
      background: 'transparent',
      border: 'none',
      fontSize: '1.25rem',
      color: theme?.text?.secondary || '#6B7280',
      cursor: 'pointer',
      padding: '0.5rem'
    }
  };

  const handleManageClick = async (e) => {
    e.preventDefault();
    if (paymentMode === 'stripe') {
      try {
        const url = await onManageSubscription(e);
        if (url) {
          window.location.href = url;
        }
      } catch (error) {
        console.error('Failed to get portal URL:', error);
      }
    } else {
      setShowInstructions(true);
    }
  };

  return (
    <>
      <motion.div
        style={styles.container}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <style>{`
          @keyframes pulse {
            0% { box-shadow: 0 0 0 0 ${theme?.primary?.orange?.[400]}40; }
            70% { box-shadow: 0 0 0 10px ${theme?.primary?.orange?.[400]}00; }
            100% { box-shadow: 0 0 0 0 ${theme?.primary?.orange?.[400]}00; }
          }
        `}</style>
        <div style={styles.backgroundPattern} />
        
        <div style={styles.content}>
          <div style={styles.header}>
            <h2 style={styles.title}>Premium Subscription</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={styles.planBadge}>{plan || 'Premium'}</span>
              <FontAwesomeIcon icon={faCrown} style={styles.crownIcon} />
            </div>
          </div>
          
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {/* Platform */}
            <div style={styles.row}>
            <div style={styles.label}>
              <FontAwesomeIcon icon={faCreditCard} />
              <span>Platform</span>
            </div>
            <div style={styles.platformInfo}>
              <FontAwesomeIcon 
                icon={platformInfo.icon} 
                style={{ ...styles.platformIcon, color: platformInfo.color }} 
              />
              <span style={styles.value}>{platformInfo.name}</span>
            </div>
          </div>

          {/* Status */}
          <div style={styles.row}>
            <div style={styles.label}>
              <FontAwesomeIcon icon={faInfoCircle} />
              <span>Status</span>
            </div>
            <span style={{ 
              ...styles.value, 
              color: isExpired ? '#DC2626' : isExpiringSoon ? '#F59E0B' : '#059669'
            }}>
              {isExpired ? 'Expired' : isExpiringSoon ? 'Expiring Soon' : 'Active'}
            </span>
          </div>

          {/* Expiry/Renewal Date */}
          <div style={styles.row}>
            <div style={styles.label}>
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span>{isRecurring ? 'Next renewal' : 'Expires on'}</span>
            </div>
            <span style={{ 
              ...styles.value, 
              color: isExpired ? '#DC2626' : isExpiringSoon ? '#F59E0B' : theme?.text?.primary || '#111827' 
            }}>
              {expiryDate.format('MMM DD, YYYY')}
              {!isExpired && ` (${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} left)`}
            </span>
          </div>

          {/* Billing Type */}
          <div style={styles.row}>
            <div style={styles.label}>
              <FontAwesomeIcon icon={faInfoCircle} />
              <span>Billing</span>
            </div>
            <span style={styles.value}>
              {isRecurring ? 'Recurring' : 'One-time'}
            </span>
          </div>

          {/* Period (for license codes) */}
          {period && (
            <div style={styles.row}>
              <div style={styles.label}>
                <FontAwesomeIcon icon={faCalendarAlt} />
                <span>Duration</span>
              </div>
              <span style={styles.value}>
                {period} {period === 1 ? 'month' : 'months'}
              </span>
            </div>
          )}

          {/* License Code (for bulk purchases) */}
          {licenseCode && (
            <div style={styles.row}>
              <div style={styles.label}>
                <FontAwesomeIcon icon={faKey} />
                <span>License Code</span>
              </div>
              <span style={styles.value}>
                {licenseCode}
              </span>
            </div>
          )}

          {/* Assigned Email (for bulk purchases) */}
          {assigned_email && (
            <div style={styles.row}>
              <div style={styles.label}>
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>Assigned To</span>
              </div>
              <span style={styles.value}>
                {assigned_email}
              </span>
            </div>
          )}

          {/* Manage Subscription */}
          {(paymentMode === 'stripe' || platformInfo.instructions.length > 0) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center' }}>
              <motion.button
                style={styles.manageButton}
                whileHover={{ 
                  scale: 1.02, 
                  transform: 'translateY(-2px)',
                  boxShadow: theme?.shadows?.md || '0 4px 12px rgba(0,0,0,0.15)'
                }}
                whileTap={{ scale: 0.98 }}
                onClick={handleManageClick}
                disabled={loading}
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} style={styles.buttonIcon} />
                {loading ? 'Opening Portal...' : 'Manage Subscription'}
                <FontAwesomeIcon icon={faExternalLinkAlt} style={styles.buttonIcon} />
              </motion.button>
              
              
            </div>
          )}
          </div>
        </div>
      </motion.div>

      {/* Instructions Modal */}
      {showInstructions && (
        <motion.div
          style={styles.instructionsModal}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowInstructions(false)}
        >
          <motion.div
            style={styles.instructionsContent}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={styles.closeButton}
              onClick={() => setShowInstructions(false)}
            >
              ×
            </button>
            
            <div style={styles.instructionsHeader}>
              <FontAwesomeIcon 
                icon={platformInfo.icon} 
                style={{ fontSize: '1.5rem', color: platformInfo.color }} 
              />
              <h3 style={{ margin: 0, color: theme?.text?.primary || '#111827' }}>
                Manage Subscription on {platformInfo.name}
              </h3>
            </div>

            <ol style={styles.instructionsList}>
              {platformInfo.instructions.map((instruction, index) => (
                <li key={index} style={styles.instructionItem}>
                  <div style={styles.instructionNumber}>
                    {index + 1}
                  </div>
                  <p style={styles.instructionText}>
                    {instruction}
                  </p>
                </li>
              ))}
            </ol>
          </motion.div>
        </motion.div>
      )}

    </>
  );
};

export default PremiumDetails;
